// The animated wake behind an ambient ship (AmbientSea).
//
// The wake is not a ribbon of geometry that is animated on the CPU. It is a
// fixed field of foam tufts — flat quads lying on the water — plus a per-vertex
// `aTuft` attribute, and the vertex shader (wakeMaterial) is what makes them
// move: each tuft is born at the stern, travels back along the ship's track,
// spreads out along its arm and dissolves. One draw call for every ship in the
// plan, and no per-frame CPU work beyond the instance matrix the ship already
// writes.
//
// Why not the old three foam boxes? Two boxes in a V and a centre bar read as
// exactly what they were — three straight white lines stuck behind the hull
// (owner's real-GPU note, 2026-10-22). A wake is turbulent; the tufts carry the
// V shape while the animation carries the motion.
//
// Attributes:
//   position  the quad corner in the XZ plane (a real quad, so the buffer is
//             well-formed for anything that inspects it)
//   aCorner   the same corner, [-0.5, 0.5] — the shader scales it and uses it
//             for the soft radial falloff
//   aTuft     vec4 (phase 0..1, lateral -1..1, spread, size)
//
// Ship frame: forward is +X, so the trail runs back along -X from the stern.

import * as THREE from 'three';

/** Foam tufts at each side of the wake (half the field). */
const ARM = 16;
/** Extra tufts along the centre, so the plume is filled not hollow. */
const CHURN = 8;

/** Deterministic 0..1 jitter — the buffer is built once, so it must not vary. */
function jitter(k: number, salt: number): number {
  const v = Math.sin(k * 12.9898 + salt * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

/**
 * Lateral position of each tuft across the wake, -1 (port arm) .. +1
 * (starboard arm). Tufts are NOT placed on two ruled tracks: the field fills
 * the widening triangle, weighted toward the arms because a wake is brightest
 * along them. Placing them on tracks made the foam read as straight white bars
 * (owner's note, 2026-10-22).
 */
function laterals(total: number): number[] {
  const out: number[] = [];
  for (let k = 0; k < total; k++) {
    const signed = jitter(k, 4) * 2 - 1;
    // Push toward the edges while still filling the middle.
    out.push(Math.sign(signed) * Math.abs(signed) ** 0.65);
  }
  return out;
}

export function buildWakeGeometry(): THREE.BufferGeometry {
  const total = ARM * 2 + CHURN;
  const order = laterals(total);
  const pos: number[] = [];
  const corner: number[] = [];
  const tuft: number[] = [];
  const idx: number[] = [];
  const QUAD: ReadonlyArray<readonly [number, number]> = [
    [-0.5, -0.5],
    [0.5, -0.5],
    [0.5, 0.5],
    [-0.5, 0.5],
  ];
  for (let k = 0; k < total; k++) {
    const lateral = order[k]!;
    // The plume widens as it ages. Outer tufts are a little larger so the arms
    // read without a hard line.
    const spread = 0.3 + jitter(k, 1) * 0.18;
    const size = 0.13 + jitter(k, 2) * 0.05 + Math.abs(lateral) * 0.03;
    // Stagger the births evenly over one cycle; aTuft.x carries the offset.
    const phase = k / total;
    const base = pos.length / 3;
    for (const [cx, cz] of QUAD) {
      pos.push(cx, 0, cz);
      corner.push(cx, cz);
      tuft.push(phase, lateral, spread, size);
    }
    // Wind the quad counter-clockwise seen from above (+Y). The corners are
    // listed clockwise in the XZ plane, so the natural order faces DOWN and the
    // default back-face culling throws every tuft away.
    idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('aCorner', new THREE.Float32BufferAttribute(corner, 2));
  g.setAttribute('aTuft', new THREE.Float32BufferAttribute(tuft, 4));
  g.setIndex(idx);
  // The shader places every vertex, so the authored bounds mean nothing.
  g.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 4);
  return g;
}
